import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import fetchAPI from '../../../claims/accounting/api'

export default function ClaimWorkVisits({ id }: { id: string | number }) {
  const [visits, setVisits] = useState<any[]>([])
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editor, setEditor] = useState<any>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [form] = Form.useForm()
  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, options] = await Promise.all([
        fetchAPI(`/api/claim-work/${id}/visits`),
        fetchAPI('/api/claim-works/create-options?include_inactive=1'),
      ])
      setVisits(data.visits || [])
      setUsers(options.engineers || [])
    } catch (e: any) {
      setError(e.message || 'Не удалось загрузить выезды')
    } finally {
      setLoading(false)
    }
  }, [id])
  useEffect(() => {
    load()
  }, [load])
  const edit = (visit: any = null) => {
    form.resetFields()
    setError(null)
    if (visit) form.setFieldsValue({ ...visit, visited_on: dayjs(visit.visited_on) })
    else form.setFieldsValue({ visited_on: dayjs() })
    setEditor(visit)
  }
  const save = async () => {
    if (saving) return
    let values
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    setError(null)
    try {
      await fetchAPI(`/api/claim-work/${id}/visits`, {
        method: 'PUT',
        body: JSON.stringify({
          ...values,
          visited_on: values.visited_on.format('YYYY-MM-DD'),
          revision: editor?.revision || 0,
          visit_id: editor?.id,
        }),
      })
      setEditor(undefined)
      await load()
      message.success('Фактический выезд сохранён')
    } catch (e: any) {
      setError(e.message || 'Не удалось сохранить выезд')
    } finally {
      setSaving(false)
    }
  }
  const remove = async (row: any) => {
    try {
      await fetchAPI(`/api/claim-work/${id}/visits/${row.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ revision: row.revision }),
      })
      await load()
    } catch (e: any) {
      setError(e.message || 'Не удалось убрать выезд')
    }
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        Укажите фактические даты и всех выезжавших мастеров. Несколько работ одного мастера за день
        учитываются как один день. Дата завершения работы сюда не подставляется.
      </Typography.Text>
      {error && (
        <Alert
          type="error"
          message={error}
          action={
            <Button disabled={loading || saving} onClick={load}>
              Обновить
            </Button>
          }
        />
      )}
      <Button icon={<PlusOutlined />} onClick={() => edit()}>
        Добавить выезд
      </Button>
      <Table
        size="small"
        rowKey="id"
        dataSource={visits}
        loading={loading}
        pagination={false}
        columns={[
          {
            title: 'Дата выезда',
            dataIndex: 'visited_on',
            render: (value, row) => (
              <Button type="link" style={{ padding: 0 }} onClick={() => edit(row)}>
                {dayjs(value).format('DD.MM.YYYY')}
              </Button>
            ),
          },
          {
            title: 'Мастера',
            dataIndex: 'engineer_ids',
            render: (ids: number[]) =>
              ids.map(id => users.find(u => u.id === id)?.name || `Сотрудник №${id}`).join(', '),
          },
          { title: 'Комментарий', dataIndex: 'comment' },
          {
            title: '',
            key: 'remove',
            render: (_, row) => (
              <Popconfirm
                title="Убрать выезд из учёта?"
                okText="Убрать"
                cancelText="Отмена"
                onConfirm={() => remove(row)}
              >
                <Button type="text" danger>
                  Убрать
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
      <Modal
        title={editor ? 'Изменить выезд' : 'Фактический выезд'}
        open={editor !== undefined}
        onCancel={() => !saving && setEditor(undefined)}
        onOk={save}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        maskClosable={false}
        destroyOnHidden
      >
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <Form form={form} layout="vertical">
          <Form.Item name="visited_on" label="Фактическая дата" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" disabledDate={date => date.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item
            name="engineer_ids"
            label="Выезжавшие мастера"
            rules={[{ required: true, type: 'array', min: 1 }]}
          >
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={users.map(user => ({ value: user.id, label: user.name }))}
            />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}
